import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { apiClient, axiosClient, useAuth } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { PhysicalFilesIcon } from '../components/NavIcons';
import {
  Button, Card, Input, Select, StatusPill, Tabs, Table, EmptyState, Skeleton, Modal, Menu, useToast,
} from '../../design-system';
import ContactManager from '../components/client/ContactManager';
import GstinManager from '../components/client/GstinManager';
import BankAccountManager from '../components/client/BankAccountManager';
import SignatoryManager from '../components/client/SignatoryManager';
import TurnoverManager from '../components/client/TurnoverManager';
import ExpirablesManager from '../components/client/ExpirablesManager';
import ClientRequestsManager from '../components/client/ClientRequestsManager';
import ClientNotes from '../components/client/ClientNotes';
import TallyPanel from '../components/TallyPanel';
import { describeProposalWindow } from '../../lib/leadDays';
import {
  formatServiceName,
  resolveEffectiveDueRule,
  shortDueRule,
  describeDueRule,
} from '../../lib/serviceCatalog';
import '../styles/ClientDetail.css';
// The card view of the service picker reuses the Services page's group-card
// styles verbatim, so the two pages present a category the same way.
import '../styles/OrgAdminServices.css';
// .client-file-badge — the physical file number shown under the client name.
import '../styles/PhysicalFiles.css';

const ADMIN_LEVEL = 5;

const FREQUENCY_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  annual: 'Annual',
  one_time: 'One-Time',
};

const CLIENT_TYPE_LABELS = {
  individual: 'Individual',
  proprietorship: 'Proprietorship',
  partnership: 'Partnership',
  llp: 'LLP',
  private_ltd: 'Private Ltd',
  public_ltd: 'Public Ltd',
  huf: 'HUF',
  trust: 'Trust',
  society: 'Society',
};

const HOLDER_TYPE_LABELS = { individual: 'Individual', corporate: 'Corporate', trust: 'Trust', fund: 'Fund' };
const SHARE_CLASS_LABELS = { equity: 'Equity', preference: 'Preference', ordinary: 'Ordinary', founder: 'Founder', other: 'Other' };
const GROUP_RELATION_LABELS = { subsidiary: 'Subsidiary', associate: 'Associate', jv: 'Joint Venture', branch: 'Branch' };

const BOARD_ROLE_LABELS = {
  chairperson: 'Chairperson', vice_chairperson: 'Vice/Deputy Chairperson', managing_director: 'Managing Director',
  executive_director: 'Executive Director', whole_time_director: 'Whole-Time Director', non_executive_director: 'Non-Executive Director',
  independent_director: 'Independent Director', nominee_director: 'Nominee Director', alternate_director: 'Alternate Director',
  additional_director: 'Additional Director', promoter_director: 'Promoter Director',
  company_secretary: 'Company Secretary',
};
const COMMITTEE_TYPE_LABELS = {
  audit: 'Audit', nomination_remuneration: 'Nomination & Remuneration', risk_management: 'Risk Management',
  stakeholders_relationship: 'Stakeholders Relationship', csr: 'CSR', esg: 'ESG / Sustainability', executive: 'Executive', other: 'Other',
};
const OFFICER_TYPE_LABELS = {
  kmp_md_ceo: 'KMP — MD / CEO', kmp_cfo: 'KMP — CFO', kmp_company_secretary: 'KMP — Company Secretary',
  authorised_signatory: 'Authorised Signatory', resident_director: 'Resident Director', compliance_officer: 'Compliance Officer',
  data_protection_officer: 'Data Protection Officer', nodal_officer: 'Nodal Officer', posh_presiding_officer: 'POSH Presiding Officer', other: 'Other',
};
const UNIT_TYPE_LABELS = {
  division: 'Division', department: 'Department', team: 'Team', cost_centre: 'Cost Centre', branch: 'Branch', other: 'Other',
};
const EMPLOYMENT_TYPE_LABELS = {
  full_time: 'Full-Time', part_time: 'Part-Time', contract: 'Contract', consultant: 'Consultant', intern: 'Intern', other: 'Other',
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

// Depth of a unit in the parent tree (for indentation), with a visited guard so
// bad data (a cycle) can't loop forever.
const unitDepth = (unit, byKey) => {
  let depth = 0;
  const seen = new Set();
  let cur = unit;
  while (cur && cur.parentKey && byKey[cur.parentKey] && !seen.has(cur.key)) {
    seen.add(cur.key);
    cur = byKey[cur.parentKey];
    depth += 1;
  }
  return depth;
};

// ₹ with Indian digit grouping; '-' for empty.
const formatINR = (n) => (n == null ? '-' : `₹${Number(n).toLocaleString('en-IN')}`);
const formatPct = (n) => (n == null ? '-' : `${n}%`);

// ⚠️ MASKED ON THE READ VIEW, in full only on the edit form. UIDAI's own
// guidance is that a stored Aadhaar is shown as its last four digits, and a
// client page is left open on a shared screen all day. Anyone who actually
// needs to key it into a portal opens the form, which is a deliberate act.
const maskAadhaar = (v) => {
  const digits = String(v || '').replace(/\D/g, '');
  return digits.length === 12 ? `XXXX XXXX ${digits.slice(-4)}` : (v || '-');
};

const COMPANY_CATEGORY_LABELS = {
  company_limited_by_shares: 'Company limited by shares',
  company_limited_by_guarantee: 'Company limited by guarantee',
  unlimited_company: 'Unlimited company',
};
const COMPANY_SUB_CATEGORY_LABELS = {
  non_govt_company: 'Non-govt company',
  state_govt_company: 'State govt company',
  union_govt_company: 'Union govt company',
  subsidiary_of_foreign_company: 'Subsidiary of a foreign company',
  guarantee_and_association_company: 'Guarantee and association company',
};
const CHARGE_TYPE_LABELS = {
  hypothecation: 'Hypothecation', mortgage: 'Mortgage', pledge: 'Pledge',
  lien: 'Lien', assignment: 'Assignment', other: 'Other',
};

// Which panels a client of this shape should be ASKED for. Used to decide
// whether a card appears at all — not whether it has anything in it. A card
// that hides until it is filled can never be filled, because nobody learns it
// exists; the Registrations card has always shown its dashes for the same
// reason. So the shape of the client decides, and emptiness only decides what
// the rows say.
const NATURAL_PERSON_TYPES = ['individual', 'proprietorship', 'huf'];
const ROC_TYPES = ['private_ltd', 'public_ltd', 'llp'];
const isNaturalPerson = (c) => NATURAL_PERSON_TYPES.includes(c?.clientType);
const filesWithRoc = (c) => ROC_TYPES.includes(c?.clientType);

// One line from a structured address; blanks are skipped so a half-filled
// record doesn't render as ", , ".
const formatAddress = (a) => {
  if (!a) return '';
  const tail = [a.city, a.state, a.pincode].filter(Boolean).join(' ');
  return [a.line1, a.line2, tail].filter(Boolean).join(', ');
};

const fadeVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};


// ─── The five groups, and which panel lives in each ─────────────────────────
//
// ⚠️ FIFTEEN TOP-LEVEL TABS IS THE BUG THIS FIXES, and it was not a cosmetic
// one: `Tabs.css` scrolls its strip with the scrollbar hidden, so the last few
// were invisible on any normal window — which is exactly how "🔒 Notes" came to
// read as a feature that was never built (see the note on that file). Grouping
// is what keeps the strip inside one screen for good, rather than moving the
// problem along by one tab every time something is added.
//
// ⚠️ THE LEAF KEYS ARE UNCHANGED. Every `{tab === 'signatories' && …}` block
// below still keys off the same string it always did; only the navigation above
// them is new. A rename would have meant touching seventeen render blocks to
// reorganise a tab strip, which is how a layout change turns into a bug hunt.
//
// ⚠️ 'notes' IS DELIBERATELY IN NO GROUP. It is passive commentary, not a
// section of the record, and it is what somebody reopens constantly while
// working a client — so it is a panel on Overview instead (permission and all).
// Filing it under a group would bury it a second time.
// What Overview shows at a glance, and where each card goes.
//
// ⚠️ `actionable` MARKS THE TWO THAT SOMEBODY HAS TO DO SOMETHING ABOUT — the
// same two that earn a badge on the parent tab strip. They are highlighted only
// when non-zero: a permanent red "0 open requests" teaches people to stop
// reading the colour.
const CLIENT_STATS = [
  { key: 'gstins', label: 'GSTINs', leaf: 'gstins' },
  { key: 'contacts', label: 'Contacts', leaf: 'contacts' },
  { key: 'signatories', label: 'Signatories', leaf: 'signatories' },
  { key: 'bank', label: 'Bank accounts', leaf: 'bank' },
  { key: 'enrollments', label: 'Enrolled', leaf: 'enrollments' },
  { key: 'expirables', label: 'Expiring soon', leaf: 'expirables', actionable: true },
  { key: 'requests', label: 'Open requests', leaf: 'requests', actionable: true },
];

const TAB_GROUPS = [
  { key: 'overview', label: 'Overview', children: ['overview'] },
  // All five answer "who is connected to this client". They are separate lists
  // in the database but one question to the person reading the page.
  { key: 'people', label: 'People', children: ['contacts', 'governance', 'signatories', 'ubo', 'orgchart'] },
  // Identifiers and credentials issued TO the client, each with validity dates.
  // ⚠️ `charges` lives HERE rather than under Financials. A charge is a
  // statutory register kept with the RoC, filed and satisfied on forms
  // (CHG-1 / CHG-4) by the same people who do MGT-7 and AOC-4 — company-law
  // work, not money work. Financials is where the client's own numbers live.
  { key: 'registrations', label: 'Registrations', children: ['gstins', 'expirables', 'charges'] },
  // Tally sits here rather than in its own tab: it is where this client's
  // numbers come FROM, which is a financial fact about them.
  { key: 'financials', label: 'Financials', children: ['bank', 'turnover', 'tally'] },
  // One workflow read left to right: what the client is enrolled for → the
  // periods that generates → the requests raised against those periods.
  { key: 'compliance', label: 'Compliance', children: ['enrollments', 'calendar', 'requests'] },
];

const LEAF_LABELS = {
  overview: 'Overview',
  contacts: 'Contacts',
  governance: 'Governance',
  signatories: 'Signatories',
  ubo: 'Beneficial Owners',
  orgchart: 'Org Chart',
  gstins: 'GSTINs',
  expirables: 'Licenses & DSCs',
  charges: 'Charges',
  bank: 'Bank Accounts',
  turnover: 'Turnover & Audit',
  tally: 'Tally',
  enrollments: 'Enrolled',
  calendar: 'Calendar',
  requests: 'Requests',
};

// leaf key → its group key. Built from the list above rather than written out,
// so a panel moved between groups cannot be left pointing at the old one.
const GROUP_OF = Object.fromEntries(
  TAB_GROUPS.flatMap((g) => g.children.map((leaf) => [leaf, g.key]))
);

// ⚠️ IF A GROUP EVER HOLDS MORE THAN THIS, SPLIT IT BACK OUT rather than letting
// the sub-strip start scrolling too — that is the same failure one level down.
const MAX_CHILDREN_PER_GROUP = 5;

// Said out loud at startup in development rather than left as a comment
// nobody re-reads. The sub-strip scrolls exactly like the parent one does, so
// a sixth child hides itself the same way the fifteenth tab did — and that
// failure is invisible on a wide monitor, which is where it gets added.
if (process.env.NODE_ENV === 'development') {
  TAB_GROUPS.filter((g) => g.children.length > MAX_CHILDREN_PER_GROUP).forEach((g) => {
    // eslint-disable-next-line no-console
    console.warn(
      `ClientDetail: the "${g.label}" tab now holds ${g.children.length} panels. `
      + `Past ${MAX_CHILDREN_PER_GROUP} the sub-tab strip starts scrolling and hiding its own `
      + 'contents — split the group instead of adding to it.'
    );
  });
}

function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  /**
   * Open the KYC photograph.
   *
   * ⚠️ NOT A PLAIN <a href>. Every /api/documents route needs the bearer
   * token, so a bare link 401s and the photo looks missing. This takes the
   * same path DocumentsManager does — ask for a short-lived signed URL and
   * open that, falling back to a blob for a locally-stored file.
   */
  const openPhoto = async (photo) => {
    const docId = photo?._id || photo;
    if (!docId) return;
    try {
      const res = await apiClient.get(`/api/documents/${docId}/url`, { params: { inline: 'true' } });
      if (res.data.source === 'stream') {
        const blob = await apiClient.get(`/api/documents/${docId}/download`, { responseType: 'blob', params: { inline: 'true' } });
        window.open(URL.createObjectURL(blob.data), '_blank', 'noopener');
        return;
      }
      window.open(res.data.url, '_blank', 'noopener');
    } catch {
      toast.danger('Could not open the photograph');
    }
  };
  const canManageServices = (user?.role?.level || 0) >= ADMIN_LEVEL;
  // Internal notes are their own permission (Admin + Partner by default), not a
  // role level — a firm routinely wants a compliance partner reading them
  // without being made an Admin. The server gates the API regardless; this only
  // decides whether the tab is offered.
  const canReadNotes = !!user?.permissions?.includes('client-notes.access');
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // ─── Where we are, kept in the URL ────────────────────────────────────────
  //
  // ⚠️ IN THE QUERY, NOT IN STATE — the rule `DocumentsBrowser` already follows,
  // and for the same reason: Back and Forward should walk the sections a person
  // actually visited, and a link to "this client's GSTINs" should survive being
  // pasted to a colleague. It was plain `useState` before, so every one of the
  // fifteen sections shared one address and none of them could be linked to.
  const [searchParams, setSearchParams] = useSearchParams();

  // ⚠️ A LEAF KEY IN ?tab= IS HONOURED, NOT REJECTED. Old links (and anything
  // somebody typed from memory) name the panel — `?tab=signatories` — with no
  // idea that groups now exist. Resolving it to its group is what makes this a
  // redirect rather than a broken bookmark; the alternative dumps them on
  // Overview with no clue why.
  const rawTab = searchParams.get('tab') || '';
  const rawSub = searchParams.get('sub') || '';
  const group = GROUP_OF[rawTab] || (TAB_GROUPS.some((g) => g.key === rawTab) ? rawTab : 'overview');
  const groupDef = TAB_GROUPS.find((g) => g.key === group) || TAB_GROUPS[0];
  // The leaf is whichever of the three sources names a panel that really lives
  // in this group: an explicit ?sub=, a ?tab= that was itself a leaf, else the
  // group's first child. Never a leaf from a DIFFERENT group, or the sub-strip
  // would show nothing selected while a panel from elsewhere rendered below it.
  const tab = groupDef.children.includes(rawSub)
    ? rawSub
    : (groupDef.children.includes(rawTab) ? rawTab : groupDef.children[0]);

  // Open a panel by its leaf key. Everything that used to call `setTab` calls
  // this, so a caller still names only the panel it wants — the group is looked
  // up, never passed in, and cannot be got wrong.
  const setTab = (leaf) => {
    const g = GROUP_OF[leaf] || 'overview';
    // The functional form so an unrelated query param on the URL survives.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', g);
      next.set('sub', leaf);
      return next;
    });
  };

  // Switching GROUP lands on its first panel. Deliberately not "the panel you
  // were last on in that group": a tab strip that reopens somewhere different
  // each time is a strip you have to read before every click.
  const setGroup = (key) => {
    const g = TAB_GROUPS.find((x) => x.key === key);
    if (g) setTab(g.children[0]);
  };

  // ─── The two counts that are worth a badge on the PARENT ──────────────────
  //
  // ⚠️ COUNTS BELONG ON THE SUB-TABS; these two are the stated exception because
  // they are ACTIONABLE — a request nobody has answered and a licence about to
  // lapse are things somebody must do, and burying them one click down is how
  // the grouping would lose information the flat strip was at least showing.
  // "23 contacts" is not news and gets no parent badge.
  //
  // ⚠️ FETCHED HERE RATHER THAN REPORTED UP BY THE PANELS. Those only mount when
  // their own tab is open, so a badge fed by them would appear only after you
  // had already gone and looked — which is the one moment it is of no use.
  // Collapsed by default — see the panel on Overview for why it is not a tab.
  const [notesOpen, setNotesOpen] = useState(false);
  const [openRequestCount, setOpenRequestCount] = useState(null);
  const [expiringCount, setExpiringCount] = useState(null);

  useEffect(() => {
    let live = true;
    // ⚠️ A BADGE IS A COURTESY AND MUST NEVER BREAK THE PAGE. Both failures are
    // swallowed: the count simply stays null and no badge renders, which is the
    // same as this page looked before it had one.
    apiClient
      .get(`/api/clients/${id}/requests`)
      .then((res) => {
        if (!live) return;
        const open = (res.data || []).filter((r) => r.status === 'open' || r.status === 'in_progress');
        setOpenRequestCount(open.length);
      })
      .catch(() => {});
    // `state=open` is resolved server-side (lapsed / due today / this week /
    // soon) — re-deriving it in the browser would be a second definition of
    // "needs attention" free to disagree with the Renewals screen.
    apiClient
      .get('/api/expirables', { params: { clientId: id, state: 'open' } })
      .then((res) => { if (live) setExpiringCount((res.data || []).length); })
      .catch(() => {});
    return () => { live = false; };
  }, [id]);


  const [enrollments, setEnrollments] = useState([]);
  const [services, setServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  // Two ways to pick the same services: the compact scrolling list, or the
  // category cards (the same layout as the Services page, so the two read the
  // same). Purely a view preference — both write to selectedServiceIds.
  const [servicePickerView, setServicePickerView] = useState('list'); // 'list' | 'cards'
  // The picker is a long list, so it stays collapsed until the search box is
  // clicked — the rest of the enrolment form (preparer, hours, submit) is then
  // right there instead of being pushed off-screen.
  const [servicePickerOpen, setServicePickerOpen] = useState(false);
  const servicePickerRef = useRef(null);
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [addServiceForm, setAddServiceForm] = useState({ groupName: '', name: '' });
  const [addServiceError, setAddServiceError] = useState('');
  const [addServiceSaving, setAddServiceSaving] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [enrollForm, setEnrollForm] = useState({
    selectedServiceIds: [],
    gstinId: '',
    defaultPreparerId: '',
    defaultPreparerHours: '',
    defaultReviewerId: '',
    defaultReviewerHours: '',
    defaultAssistantIds: [],
    defaultAssistantHours: {},
  });
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  const [instances, setInstances] = useState([]);
  const [instanceFilter, setInstanceFilter] = useState('all');

  // Beneficial-ownership report — computed server-side, fetched lazily the first
  // time the tab is opened (ownership rarely changes mid-view).
  const [ubo, setUbo] = useState(null);
  const [uboLoading, setUboLoading] = useState(false);
  const [physicalFiles, setPhysicalFiles] = useState([]);

  useEffect(() => {
    fetchClient();
    fetchEnrollments();
    fetchInstances();
    fetchServices();
    setUbo(null); // drop the previous client's beneficial-ownership report
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
    apiClient
      .get('/api/physical-files', { params: { clientId: id } })
      .then((res) => setPhysicalFiles(res.data))
      .catch(() => setPhysicalFiles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch the UBO report on first open of its tab. Refetches when the client id
  // changes; ownership edits happen on another page, so staleness within a view
  // is fine.
  useEffect(() => {
    if (tab !== 'ubo' || ubo) return;
    setUboLoading(true);
    apiClient
      .get(`/api/clients/${id}/ubo`)
      .then((res) => setUbo(res.data))
      .catch(() => {})
      .finally(() => setUboLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  const fetchServices = async () => {
    try {
      const res = await apiClient.get('/api/services');
      setServices(res.data);
    } catch (err) {
      // non-fatal; the enroll form's service picker just stays empty
    }
  };

  const fetchClient = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/clients/${id}`);
      setClient(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const res = await apiClient.get(`/api/clients/${id}/enrollments`);
      setEnrollments(res.data);
    } catch (err) {
      // non-fatal; overview/contacts/gstins tabs still work
    }
  };

  const fetchInstances = async () => {
    try {
      const res = await apiClient.get('/api/instances', { params: { clientId: id } });
      setInstances(res.data);
    } catch (err) {
      // non-fatal; other tabs still work
    }
  };

  const updateInstance = async (instanceId, updates) => {
    try {
      const res = await apiClient.put(`/api/instances/${instanceId}`, updates);
      setInstances((prev) => prev.map((i) => (i._id === instanceId ? res.data : i)));
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update compliance instance');
    }
  };

  const isOverdue = (instance) =>
    instance.status !== 'filed' &&
    instance.status !== 'not_applicable' &&
    instance.statutoryDueDate &&
    new Date(instance.statutoryDueDate) < new Date();

  const visibleInstances =
    instanceFilter === 'all' ? instances : instances.filter((i) => i.status === instanceFilter);

  const handleEnrollChange = (e) => {
    const { name, value } = e.target;
    setEnrollForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleEnrollService = (serviceId) => {
    setEnrollForm((prev) => ({
      ...prev,
      selectedServiceIds: prev.selectedServiceIds.includes(serviceId)
        ? prev.selectedServiceIds.filter((existingId) => existingId !== serviceId)
        : [...prev.selectedServiceIds, serviceId],
    }));
  };

  const serviceGroups = (() => {
    const term = serviceSearch.trim().toLowerCase();
    const enabledServices = services.filter((s) => s.isEnabled !== false);
    const filtered = term
      ? enabledServices.filter(
          (s) => s.name.toLowerCase().includes(term) || s.groupName.toLowerCase().includes(term)
        )
      : enabledServices;

    const byGroup = new Map();
    filtered.forEach((s) => {
      const key = s.groupName.trim().toLowerCase();
      if (!byGroup.has(key)) byGroup.set(key, { displayName: s.groupName, items: [] });
      byGroup.get(key).items.push(s);
    });
    return Array.from(byGroup.values()).sort((a, b) => b.items.length - a.items.length);
  })();

  // Close the picker on a click outside it or on Escape. Ticking a checkbox is a
  // click INSIDE, so the panel survives selecting many services in one go — it
  // only closes when you deliberately leave it. Selections persist either way.
  useEffect(() => {
    if (!servicePickerOpen) return undefined;

    const onPointerDown = (e) => {
      if (servicePickerRef.current && !servicePickerRef.current.contains(e.target)) {
        setServicePickerOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setServicePickerOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [servicePickerOpen]);

  // Select / clear a whole category at once (the card view's "All" box). Written
  // as a set operation rather than a per-row toggle so a half-selected group
  // fills in rather than inverting each row.
  const toggleEnrollGroup = (items, checked) => {
    const ids = items.map((s) => s._id);
    setEnrollForm((prev) => ({
      ...prev,
      selectedServiceIds: checked
        ? Array.from(new Set([...prev.selectedServiceIds, ...ids]))
        : prev.selectedServiceIds.filter((id) => !ids.includes(id)),
    }));
  };

  const openAddServiceModal = () => {
    setAddServiceForm({ groupName: '', name: '' });
    setAddServiceError('');
    setAddServiceModalOpen(true);
  };

  const handleAddServiceSave = async (e) => {
    e.preventDefault();
    setAddServiceError('');

    if (!addServiceForm.groupName.trim() || !addServiceForm.name.trim()) {
      setAddServiceError('Group and service name are required');
      return;
    }

    setAddServiceSaving(true);
    try {
      const existingGroup = services.find(
        (s) => s.groupName.toLowerCase() === addServiceForm.groupName.trim().toLowerCase()
      );
      const nextItemId = services.reduce((max, s) => Math.max(max, s.itemId), 0) + 1;

      await apiClient.post('/api/services', {
        groupId: existingGroup ? existingGroup.groupId : Math.max(...services.map((s) => s.groupId), 0) + 1,
        groupName: addServiceForm.groupName.trim(),
        itemId: nextItemId,
        name: addServiceForm.name.trim(),
      });
      toast.success('Service added');
      setAddServiceModalOpen(false);
      fetchServices();
    } catch (err) {
      setAddServiceError(err.response?.data?.error || 'Failed to add service');
    } finally {
      setAddServiceSaving(false);
    }
  };

  const toggleAssistant = (userId) => {
    setEnrollForm((prev) => ({
      ...prev,
      defaultAssistantIds: prev.defaultAssistantIds.includes(userId)
        ? prev.defaultAssistantIds.filter((existingId) => existingId !== userId)
        : [...prev.defaultAssistantIds, userId],
    }));
  };

  const setAssistantHours = (userId, hours) => {
    setEnrollForm((prev) => ({
      ...prev,
      defaultAssistantHours: { ...prev.defaultAssistantHours, [userId]: hours },
    }));
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    setEnrollError('');
    setEnrolling(true);
    try {
      const defaultHourAllocations = [];
      if (enrollForm.defaultPreparerId && enrollForm.defaultPreparerHours) {
        defaultHourAllocations.push({
          userId: enrollForm.defaultPreparerId,
          role: 'preparer',
          allocatedHours: Number(enrollForm.defaultPreparerHours),
        });
      }
      if (enrollForm.defaultReviewerId && enrollForm.defaultReviewerHours) {
        defaultHourAllocations.push({
          userId: enrollForm.defaultReviewerId,
          role: 'reviewer',
          allocatedHours: Number(enrollForm.defaultReviewerHours),
        });
      }
      enrollForm.defaultAssistantIds.forEach((userId) => {
        const hours = enrollForm.defaultAssistantHours[userId];
        if (hours) {
          defaultHourAllocations.push({ userId, role: 'assistant', allocatedHours: Number(hours) });
        }
      });

      if (enrollForm.selectedServiceIds.length === 0) {
        setEnrollError('Select at least one service');
        setEnrolling(false);
        return;
      }

      const payload = {
        clientId: id,
        serviceIds: enrollForm.selectedServiceIds,
        gstinId: enrollForm.gstinId || undefined,
        defaultPreparerId: enrollForm.defaultPreparerId || undefined,
        defaultReviewerId: enrollForm.defaultReviewerId || undefined,
        defaultAssistantIds: enrollForm.defaultAssistantIds,
        defaultHourAllocations,
      };

      const res = await apiClient.post('/api/enrollments/bulk-from-services', payload);
      setEnrollForm({
        selectedServiceIds: [],
        gstinId: '',
        defaultPreparerId: '',
        defaultPreparerHours: '',
        defaultReviewerId: '',
        defaultReviewerHours: '',
        defaultAssistantIds: [],
        defaultAssistantHours: {},
      });
      setServiceSearch('');
      await fetchEnrollments();
      await fetchInstances();
      toast.success(`Enrolled in ${res.data.length} compliance head${res.data.length === 1 ? '' : 's'}`);
    } catch (err) {
      setEnrollError(err.response?.data?.error || 'Failed to enroll compliance');
    } finally {
      setEnrolling(false);
    }
  };

  const toggleEnrollmentActive = async (enrollment) => {
    try {
      await apiClient.put(`/api/enrollments/${enrollment._id}`, { isActive: !enrollment.isActive });
      await fetchEnrollments();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update enrollment');
    }
  };

  // ⚠️ SAID BEFORE, NOT AFTER. Removing a head removes every period seeded from
  // it as well, and not saying so is what made a deletion look like it had not
  // worked at all: the periods used to stay on the compliance calendar as
  // "Unknown", still counting themselves overdue.
  const removeEnrollment = async (enrollmentId) => {
    if (!window.confirm(
      'Remove this compliance enrollment?\n\n'
      + 'Every scheduled period for it is removed from the compliance calendar too.\n'
      + 'Tasks already created are kept, just unlinked.\n\n'
      + 'Periods already filed are never removed — set the enrollment to inactive '
      + 'instead if you need to keep them.'
    )) return;
    try {
      const { data } = await apiClient.delete(`/api/enrollments/${enrollmentId}`);
      await fetchEnrollments();
      toast.success(data?.periodsRemoved
        ? `Enrollment removed — ${data.periodsRemoved} period(s) cleared from the calendar`
        : 'Enrollment removed');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove enrollment');
    }
  };

  // The embedded-array managers each return the server's updated copy of their
  // array; splice it back into the client so every tab stays in sync.
  const patchClient = (field) => (value) => setClient((prev) => ({ ...prev, [field]: value }));
  const onClientChange = (updated) => setClient(updated);

  // Clients are never deleted — only marked inactive/closed (or reactivated).
  // All history is retained.
  const changeClientStatus = async (status) => {
    if (!window.confirm(`Mark "${client.displayName}" as ${status}?`)) return;
    try {
      const res = await apiClient.patch(`/api/clients/${id}/status`, { status });
      setClient(res.data);
      toast.success(`Client marked ${status}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update status');
    }
  };

  if (loading) {
    return (
      <OrgAdminLayout title="Client Details" subtitle="">
        <Card><Skeleton.Text lines={4} /></Card>
      </OrgAdminLayout>
    );
  }

  if (error || !client) {
    return (
      <OrgAdminLayout title="Client Details" subtitle="">
        <div className="form-error" role="alert">{error || 'Client not found'}</div>
      </OrgAdminLayout>
    );
  }

  // How many rows each panel holds, for the badge on its sub-tab. Keyed by leaf
  // so the strip below can render a group's children without knowing what any of
  // them contain.
  const leafCounts = {
    contacts: client.contacts.length,
    gstins: client.gstins.length,
    bank: (client.bankAccounts || []).length,
    signatories: (client.signatories || []).length,
    governance: (client.board || []).length + (client.committees || []).length + (client.statutoryOfficers || []).length,
    orgchart: (client.orgUnits || []).length + (client.employees || []).length,
    turnover: (client.turnoverByFY || []).length,
    enrollments: enrollments.length,
    calendar: instances.length,
    expirables: expiringCount ?? undefined,
    // The count is the OPEN ones, not every row. A satisfied charge is closed
    // history; "3" on this tab has to mean three things still encumbering the
    // company, or the number is just a row count nobody can act on.
    charges: (client.charges || []).filter((c) => !c.satisfiedOn).length,
    requests: openRequestCount ?? undefined,
  };

  // ⚠️ ONLY THE TWO ACTIONABLE FIGURES REACH THE PARENT STRIP. A parent badge
  // summing its children would read as "People 47", which answers nothing and
  // makes the two numbers that DO need answering indistinguishable from noise.
  const groupBadge = {
    compliance: openRequestCount || undefined,
    registrations: expiringCount || undefined,
  };

  // What the Overview cards show, and which panel each one opens.
  const statValues = {
    gstins: client.gstins.length,
    contacts: client.contacts.length,
    signatories: (client.signatories || []).length,
    bank: (client.bankAccounts || []).length,
    enrollments: enrollments.length,
    expirables: expiringCount,
    requests: openRequestCount,
  };

  const groupTabs = TAB_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    count: groupBadge[g.key],
  }));

  const subTabs = groupDef.children.map((leaf) => ({
    key: leaf,
    label: LEAF_LABELS[leaf] || leaf,
    count: leafCounts[leaf],
  }));

  const instanceColumns = [
    { key: 'compliance', header: 'Compliance', render: (i) => i.enrollmentId?.complianceTypeId?.name || '-' },
    { key: 'period', header: 'Period', render: (i) => i.periodLabel },
    {
      key: 'due',
      header: 'Statutory Due',
      render: (i) => (
        <input
          type="date"
          className="ds-input inline-input"
          value={i.statutoryDueDate ? i.statutoryDueDate.slice(0, 10) : ''}
          onChange={(e) => updateInstance(i._id, { statutoryDueDate: e.target.value })}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => (
        <select
          className="ds-select inline-select"
          value={i.status}
          onChange={(e) => updateInstance(i._id, { status: e.target.value })}
        >
          <option value="upcoming">Upcoming</option>
          <option value="in_progress">In Progress</option>
          <option value="filed">Filed</option>
          <option value="late_filed">Late Filed</option>
          <option value="not_applicable">Not Applicable</option>
        </select>
      ),
    },
    {
      key: 'ack',
      header: 'Ack No.',
      render: (i) => (
        <input
          type="text"
          className="ds-input inline-input"
          defaultValue={i.acknowledgmentNo || ''}
          placeholder="-"
          onBlur={(e) => {
            if (e.target.value !== (i.acknowledgmentNo || '')) {
              updateInstance(i._id, { acknowledgmentNo: e.target.value });
            }
          }}
        />
      ),
    },
    {
      key: 'remarks',
      header: 'Remarks',
      render: (i) => (
        <input
          type="text"
          className="ds-input inline-input"
          defaultValue={i.remarks || ''}
          placeholder="-"
          onBlur={(e) => {
            if (e.target.value !== (i.remarks || '')) {
              updateInstance(i._id, { remarks: e.target.value });
            }
          }}
        />
      ),
    },
  ];

  // Org-chart key -> row lookups, for resolving parent/head/manager/unit links.
  const unitByKey = Object.fromEntries((client.orgUnits || []).map((u) => [u.key, u]));
  const empByKey = Object.fromEntries((client.employees || []).map((e) => [e.key, e]));

  // ── The two header menus ──────────────────────────────────────────────────
  //
  // ACTIONS change this client's own record. UTILITIES leave the page for this
  // client's work elsewhere — every one of them carries `?client=` so it lands
  // on THIS client's rows rather than on the whole firm's list, which is the
  // difference between a shortcut and a signpost.
  const isDormant = client.status === 'inactive' || client.status === 'closed';

  const actionItems = [
    {
      key: 'edit',
      label: 'Edit client',
      description: 'Registrations, contacts, assignment',
      onSelect: () => navigate(`/org-admin/clients/${client._id}/edit`),
    },
    ...(isDormant
      ? [{
        key: 'reactivate',
        label: 'Reactivate',
        description: 'Put this client back into active work',
        onSelect: () => changeClientStatus('active'),
      }]
      : [
        {
          key: 'inactive',
          label: 'Mark inactive',
          description: 'Pause the relationship — reversible',
          onSelect: () => changeClientStatus('inactive'),
        },
        {
          key: 'close',
          label: 'Close client',
          description: 'End the engagement',
          danger: true,
          onSelect: () => changeClientStatus('closed'),
        },
      ]),
  ];

  const utilityItems = [
    {
      key: 'documents',
      label: 'Documents',
      description: "This client's document folders",
      onSelect: () => navigate(`/org-admin/documents?client=${client._id}`),
    },
    {
      key: 'tasks',
      label: 'Tasks',
      description: 'Work open against this client',
      onSelect: () => navigate(`/org-admin/tasks?client=${client._id}`),
    },
    {
      key: 'files',
      label: 'Physical files',
      description: 'Paper files held for this client',
      onSelect: () => navigate(`/org-admin/physical-files?client=${client._id}`),
    },
    {
      key: 'invoices',
      label: 'Invoices',
      description: 'Invoices, receipts and payments',
      onSelect: () => navigate(`/org-admin/finance?tab=clients&client=${client._id}`),
    },
    {
      key: 'bills',
      label: 'Bills',
      description: 'Charges on tasks, not yet invoiced',
      onSelect: () => navigate(`/org-admin/finance?tab=unbilled&client=${client._id}`),
    },
  ];

  return (
    <OrgAdminLayout title={client.displayName} subtitle={CLIENT_TYPE_LABELS[client.clientType]}>
      <div className="client-detail-page">
        <div className="detail-header">
          {client.clientCode && <span className="client-code-badge">{client.clientCode}</span>}
          {physicalFiles.map((f) => (
            <span key={f._id} className="client-file-badge" title={`Physical file${f.title ? ` — ${f.title}` : ''}${f.location ? ` (${f.location})` : ''}`}>
              <PhysicalFilesIcon />
              {f.fileNumber}
            </span>
          ))}
          <StatusPill status={client.status} />
          {/* ⚠️ TWO MENUS, NOT FOUR BUTTONS. The row used to carry Documents,
              Edit, Mark Inactive and Close Client side by side, which gave
              "Close Client" — irreversible from here — the same size and weight
              as the one people actually came to press. Grouping also names the
              difference: ACTIONS change this client's record, UTILITIES open
              this client's other work elsewhere in the app. */}
          <div className="enrollment-actions">
            <Menu label="Utilities" variant="secondary" items={utilityItems} />
            <Menu label="Actions" variant="primary" items={actionItems} />
          </div>
        </div>

        <Tabs tabs={groupTabs} active={group} onChange={setGroup} />

        {/* ⚠️ A DISTINCT `layoutId`, OR THE TWO STRIPS FIGHT. The underline is a
            framer-motion shared-layout element, so two <Tabs> on one screen
            carrying the default id animate as though they were one control and
            the marker flies between them. */}
        {subTabs.length > 1 && (
          <Tabs
            tabs={subTabs}
            active={tab}
            onChange={setTab}
            variant="pill"
            layoutId="client-detail-subtabs"
          />
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.15 }}
          >
            {tab === 'overview' && (
              <div className="tab-content">
                {/* ─── Where everything else is, and how much of it there is ───
                    Overview is the landing tab, so it is the one place that can
                    say what this client HAS without being opened. Each card is a
                    link into the panel that holds it — grouping moved those one
                    click further away, and this is what pays that back. */}
                <div className="cd-stats">
                  {CLIENT_STATS.map((s) => {
                    const value = statValues[s.key];
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={`cd-stat${s.actionable && value > 0 ? ' cd-stat-attention' : ''}`}
                        onClick={() => setTab(s.leaf)}
                      >
                        {/* A count still loading shows an em dash rather than 0 —
                            "0 open requests" and "we have not asked yet" are
                            different answers and only one of them is reassuring. */}
                        <span className="cd-stat-value">{value == null ? '—' : value}</span>
                        <span className="cd-stat-label">{s.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* ⚠️ NOTES LIVES HERE NOW, NOT ON THE TAB STRIP — passive
                    commentary rather than a section of the record, and the thing
                    somebody reopens constantly while working a client. It keeps
                    its own permission: without `client-notes.access` the panel is
                    absent entirely, never greyed out, because a disabled control
                    would itself advertise that private notes exist here. */}
                {canReadNotes && (
                  <Card>
                    <Card.Header
                      title="🔒 Notes"
                      action={
                        <Button variant="secondary" size="sm" onClick={() => setNotesOpen((v) => !v)}>
                          {notesOpen ? 'Hide' : 'Show'}
                        </Button>
                      }
                    />
                    {/* Collapsed by default so Overview stays readable, but the
                        HEADER is always on screen: the old arrangement hid this
                        behind a tab strip that scrolls with no scrollbar, and the
                        feature read as never built. */}
                    {notesOpen && <ClientNotes clientId={id} />}
                  </Card>
                )}

                <Card>
                  <Card.Header title="Registrations" />
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">PAN</span><span className="info-value">{client.pan || '-'}</span></div>
                    <div className="info-item"><span className="info-label">TAN</span><span className="info-value">{client.tan || '-'}</span></div>
                    <div className="info-item"><span className="info-label">CIN</span><span className="info-value">{client.cin || '-'}</span></div>
                    <div className="info-item"><span className="info-label">LLPIN</span><span className="info-value">{client.llpin || '-'}</span></div>
                    <div className="info-item"><span className="info-label">IEC</span><span className="info-value">{client.iec || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Udyam</span><span className="info-value">{client.udyam || '-'}</span></div>
                    <div className="info-item"><span className="info-label">PF</span><span className="info-value">{client.pf || '-'}</span></div>
                    <div className="info-item"><span className="info-label">ESIC</span><span className="info-value">{client.esic || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Professional Tax</span><span className="info-value">{client.professionalTax || '-'}</span></div>
                    <div className="info-item"><span className="info-label">LWF</span><span className="info-value">{client.lwf || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Financial Year End</span><span className="info-value">{client.financialYearEnd}</span></div>
                    {/* Pre-GST, so it only appears where one was actually
                        recorded — an empty row for a tax that no longer exists
                        is noise on every other client. */}
                    {client.vatTin && (
                      <div className="info-item"><span className="info-label">VAT / CST TIN</span><span className="info-value">{client.vatTin}</span></div>
                    )}
                  </div>
                </Card>

                {/* ⚠️ SHOWN BECAUSE THE CLIENT IS A PERSON, not because the
                    fields happen to be filled. A card that appears only once
                    somebody has filled it can never BE filled — nobody learns
                    it exists. So the client's shape decides whether the card is
                    there, and emptiness only decides whether the rows say '-'.
                    A company still never sees it: it has no date of birth. */}
                {(isNaturalPerson(client) || client.aadhaar || client.dateOfBirth || client.photoDocumentId) && (
                  <Card>
                    <Card.Header title="KYC" />
                    <div className="info-grid">
                      <div className="info-item"><span className="info-label">Aadhaar</span><span className="info-value">{maskAadhaar(client.aadhaar)}</span></div>
                      <div className="info-item"><span className="info-label">Date of Birth</span><span className="info-value">{fmtDate(client.dateOfBirth)}</span></div>
                      <div className="info-item">
                        <span className="info-label">Photograph</span>
                        <span className="info-value">
                          {client.photoDocumentId ? (
                            <button type="button" className="gst-link" onClick={() => openPhoto(client.photoDocumentId)}>
                              {client.photoDocumentId.title || 'On file'}
                            </button>
                          ) : '-'}
                        </span>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Incorporation & MCA master data. ⚠️ The incorporation date
                    is NOT the onboarding date above: it decides the first-year
                    audit exemption, the first AGM and the first ROC filing. */}
                {(filesWithRoc(client) || client.dateOfIncorporation || client.companyCategory ||
                  client.companySubCategory || client.rocCode || client.nicCode || client.businessActivity) && (
                  <Card>
                    <Card.Header title="Incorporation & MCA Master Data" />
                    <div className="info-grid">
                      <div className="info-item"><span className="info-label">Incorporated</span><span className="info-value">{fmtDate(client.dateOfIncorporation)}</span></div>
                      <div className="info-item"><span className="info-label">Category</span><span className="info-value">{COMPANY_CATEGORY_LABELS[client.companyCategory] || '-'}</span></div>
                      <div className="info-item"><span className="info-label">Sub-category</span><span className="info-value">{COMPANY_SUB_CATEGORY_LABELS[client.companySubCategory] || '-'}</span></div>
                      <div className="info-item"><span className="info-label">RoC</span><span className="info-value">{client.rocCode || '-'}</span></div>
                      <div className="info-item"><span className="info-label">NIC Code</span><span className="info-value">{client.nicCode || '-'}</span></div>
                      <div className="info-item"><span className="info-label">Business Activity</span><span className="info-value">{client.businessActivity || '-'}</span></div>
                    </div>
                  </Card>
                )}

                {/* The only address the client ITSELF has, as opposed to a GST
                    place of business. Always rendered: every client has an
                    address, and this is the one card that says where to put it. */}
                <Card>
                  <Card.Header
                    title={isNaturalPerson(client) ? 'Address' : 'Registered Office'}
                    subtitle={isNaturalPerson(client) ? undefined : 'Where the RoC serves notice'}
                  />
                  <p className="info-value">
                    {formatAddress(client.registeredAddress) || 'Not recorded — add it from the Edit Client page.'}
                  </p>
                </Card>

                <Card>
                  <Card.Header title="Assignment" />
                  <div className="info-grid">
                    <div className="info-item"><span className="info-label">Partner</span><span className="info-value">{client.assignedPartnerId?.name || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Manager</span><span className="info-value">{client.assignedManagerId?.name || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Legal Name</span><span className="info-value">{client.legalName || '-'}</span></div>
                    <div className="info-item"><span className="info-label">Onboarded</span><span className="info-value">{new Date(client.onboardingDate).toLocaleDateString()}</span></div>
                    <div className="info-item"><span className="info-label">Created By</span><span className="info-value">{client.createdBy?.name || '-'}</span></div>
                  </div>
                </Card>

                {/* Ownership & group structure (Phase 1). Read view; edited on the
                    Edit Client form. Hidden entirely when nothing is recorded. */}
                {(client.authorisedCapital != null ||
                  client.paidUpCapital != null ||
                  client.parentClientId ||
                  (client.owners || []).length > 0 ||
                  (client.groupRelations || []).length > 0) && (
                  <Card>
                    <Card.Header title="Ownership & Group Structure" />
                    <div className="info-grid">
                      <div className="info-item"><span className="info-label">Authorised Capital</span><span className="info-value">{formatINR(client.authorisedCapital)}</span></div>
                      <div className="info-item"><span className="info-label">Paid-up Capital</span><span className="info-value">{formatINR(client.paidUpCapital)}</span></div>
                      <div className="info-item">
                        <span className="info-label">Parent Company</span>
                        <span className="info-value">
                          {client.parentClientId ? (
                            <a href={`/org-admin/clients/${client.parentClientId._id}`}>{client.parentClientId.displayName}</a>
                          ) : '-'}
                        </span>
                      </div>
                    </div>

                    {(client.owners || []).length > 0 && (
                      <>
                        <h4 className="detail-subheading">Owners</h4>
                        <Table
                          columns={[
                            {
                              key: 'name',
                              header: 'Holder',
                              render: (o) => (
                                <span>
                                  {o.holderClientId ? (
                                    <a href={`/org-admin/clients/${o.holderClientId._id}`}>{o.name}</a>
                                  ) : o.name}
                                  {o.isUbo && <StatusPill variant="info" label="UBO" />}
                                  {/* Signed the memorandum at incorporation.
                                      Shown even for a holder who has since
                                      sold out, because "who subscribed?" is a
                                      question about the past. */}
                                  {o.isSubscriber && <StatusPill variant="default" label="Subscriber" />}
                                </span>
                              ),
                            },
                            { key: 'holderType', header: 'Type', render: (o) => HOLDER_TYPE_LABELS[o.holderType] || o.holderType },
                            { key: 'shareClass', header: 'Class', render: (o) => SHARE_CLASS_LABELS[o.shareClass] || o.shareClass },
                            { key: 'ownershipPct', header: 'Ownership', render: (o) => formatPct(o.ownershipPct) },
                            {
                              key: 'subscribedShares',
                              header: 'Subscribed',
                              render: (o) => (o.subscribedShares == null ? '-' : Number(o.subscribedShares).toLocaleString('en-IN')),
                            },
                            {
                              key: 'held',
                              header: 'Held',
                              render: (o) =>
                                o.disposedOn
                                  ? `${o.acquiredOn ? new Date(o.acquiredOn).toLocaleDateString() : '?'} – ${new Date(o.disposedOn).toLocaleDateString()}`
                                  : 'Current',
                            },
                          ]}
                          data={client.owners}
                        />
                      </>
                    )}

                    {(client.groupRelations || []).length > 0 && (
                      <>
                        <h4 className="detail-subheading">Group Companies</h4>
                        <Table
                          columns={[
                            {
                              key: 'child',
                              header: 'Company',
                              render: (g) => (
                                g.childClientId ? (
                                  <a href={`/org-admin/clients/${g.childClientId._id}`}>{g.childClientId.displayName}</a>
                                ) : '-'
                              ),
                            },
                            { key: 'relationType', header: 'Relation', render: (g) => GROUP_RELATION_LABELS[g.relationType] || g.relationType },
                            { key: 'ownershipPct', header: 'Ownership', render: (g) => formatPct(g.ownershipPct) },
                          ]}
                          data={client.groupRelations}
                        />
                      </>
                    )}
                  </Card>
                )}
              </div>
            )}

            {tab === 'contacts' && (
              <div className="tab-content">
                <ContactManager clientId={id} contacts={client.contacts} onChange={patchClient('contacts')} />
              </div>
            )}

            {tab === 'gstins' && (
              <div className="tab-content">
                {/* `client` is passed so a portal fetch can offer to fill the
                    client-level fields it answers (legal name, PAN) — but only
                    the ones actually BLANK. onClientPatched replaces the whole
                    record, since that write is a PUT to the client itself. */}
                <GstinManager
                  clientId={id}
                  gstins={client.gstins}
                  onChange={patchClient('gstins')}
                  client={client}
                  onClientPatched={setClient}
                />
              </div>
            )}

            {tab === 'bank' && (
              <div className="tab-content">
                <BankAccountManager clientId={id} bankAccounts={client.bankAccounts || []} onChange={patchClient('bankAccounts')} />
              </div>
            )}

            {tab === 'signatories' && (
              <div className="tab-content">
                <SignatoryManager clientId={id} signatories={client.signatories || []} onChange={patchClient('signatories')} />
              </div>
            )}

            {tab === 'governance' && (
              <div className="tab-content">
                <Card>
                  <Card.Header title="Board of Directors" />
                  {(client.board || []).length === 0 ? (
                    <EmptyState icon="🏛️" title="No board members recorded" description="Add them from the Edit Client page." />
                  ) : (
                    <Table
                      columns={[
                        {
                          key: 'name',
                          header: 'Member',
                          render: (m) => (
                            <span>
                              {m.name}
                              {m.isExecutive && <StatusPill variant="info" label="Executive" />}
                              {/* Promoter status is orthogonal to the seat, so
                                  it shows as a badge whatever the role says. */}
                              {m.isPromoter && <StatusPill variant="warning" label="Promoter" />}
                              {/* Says a holding EXISTS in the shareholder list —
                                  the shares themselves live there. Its absence is
                                  the normal case for a director. */}
                              {m.isShareholder && <StatusPill variant="success" label="Shareholder" />}
                              {m.resignedOn && <StatusPill variant="default" label="Resigned" />}
                            </span>
                          ),
                        },
                        { key: 'role', header: 'Role', render: (m) => BOARD_ROLE_LABELS[m.role] || m.role },
                        { key: 'din', header: 'DIN', render: (m) => m.din || '-' },
                        // The DIR-3 KYC pair. Aadhaar masked here for the same
                        // reason as the client's own — see maskAadhaar.
                        { key: 'aadhaar', header: 'Aadhaar', render: (m) => (m.aadhaar ? maskAadhaar(m.aadhaar) : '-') },
                        { key: 'dateOfBirth', header: 'DOB', render: (m) => fmtDate(m.dateOfBirth) },
                        { key: 'nominatedBy', header: 'Nominated By', render: (m) => m.nominatedBy || '-' },
                        { key: 'appointedOn', header: 'Appointed', render: (m) => fmtDate(m.appointedOn) },
                        { key: 'tenure', header: 'Term End / Resigned', render: (m) => (m.resignedOn ? fmtDate(m.resignedOn) : fmtDate(m.termEndOn)) },
                      ]}
                      data={client.board}
                    />
                  )}
                </Card>

                <Card>
                  <Card.Header title="Board Committees" />
                  {(client.committees || []).length === 0 ? (
                    <EmptyState icon="👥" title="No committees recorded" />
                  ) : (
                    <div className="committee-list">
                      {client.committees.map((c) => (
                        <div key={c._id} className="committee-block">
                          <div className="committee-block-head">
                            <strong>{c.name}</strong>
                            <StatusPill variant="info" label={COMMITTEE_TYPE_LABELS[c.type] || c.type} />
                            {c.formedOn && <span className="committee-formed">Formed {fmtDate(c.formedOn)}</span>}
                          </div>
                          {c.charter && <p className="committee-charter">{c.charter}</p>}
                          {(c.members || []).length > 0 && (
                            <Table
                              columns={[
                                { key: 'name', header: 'Member', render: (m) => m.name },
                                { key: 'role', header: 'Seat', render: (m) => (m.role ? m.role[0].toUpperCase() + m.role.slice(1) : '-') },
                                { key: 'from', header: 'From', render: (m) => fmtDate(m.from) },
                                { key: 'to', header: 'To', render: (m) => (m.to ? fmtDate(m.to) : 'Current') },
                              ]}
                              data={c.members}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Card>
                  <Card.Header title="Statutory Officers" />
                  {(client.statutoryOfficers || []).length === 0 ? (
                    <EmptyState icon="📋" title="No statutory officers recorded" />
                  ) : (
                    <Table
                      columns={[
                        {
                          key: 'name',
                          header: 'Officer',
                          render: (o) => <span>{o.name}{o.ceasedOn && <StatusPill variant="default" label="Ceased" />}</span>,
                        },
                        { key: 'officerType', header: 'Office', render: (o) => OFFICER_TYPE_LABELS[o.officerType] || o.officerType },
                        { key: 'din', header: 'DIN', render: (o) => o.din || '-' },
                        { key: 'appointedOn', header: 'Appointed', render: (o) => fmtDate(o.appointedOn) },
                        { key: 'ceasedOn', header: 'Ceased', render: (o) => (o.ceasedOn ? fmtDate(o.ceasedOn) : '-') },
                      ]}
                      data={client.statutoryOfficers}
                    />
                  )}
                </Card>
              </div>
            )}

            {tab === 'orgchart' && (
              <div className="tab-content">
                <Card>
                  <Card.Header title="Organisation Units" />
                  {(client.orgUnits || []).length === 0 ? (
                    <EmptyState icon="🗂️" title="No units recorded" description="Add them from the Edit Client page." />
                  ) : (
                    <Table
                      columns={[
                        {
                          key: 'name',
                          header: 'Unit',
                          // Indent by tree depth so nesting reads at a glance.
                          render: (u) => (
                            <span style={{ paddingLeft: `${unitDepth(u, unitByKey) * 16}px` }}>
                              {unitDepth(u, unitByKey) > 0 && <span className="tree-branch">↳ </span>}{u.name}
                            </span>
                          ),
                        },
                        { key: 'type', header: 'Type', render: (u) => UNIT_TYPE_LABELS[u.type] || u.type },
                        { key: 'parent', header: 'Parent', render: (u) => (u.parentKey && unitByKey[u.parentKey] ? unitByKey[u.parentKey].name : '-') },
                        { key: 'head', header: 'Head', render: (u) => (u.headEmployeeKey && empByKey[u.headEmployeeKey] ? empByKey[u.headEmployeeKey].name : '-') },
                        { key: 'cc', header: 'Cost Centre', render: (u) => u.costCentreCode || '-' },
                      ]}
                      data={client.orgUnits}
                    />
                  )}
                </Card>

                <Card>
                  <Card.Header title="People & Reporting Lines" />
                  {(client.employees || []).length === 0 ? (
                    <EmptyState icon="🧑‍💼" title="No people recorded" />
                  ) : (
                    <Table
                      columns={[
                        {
                          key: 'name',
                          header: 'Person',
                          render: (e) => <span>{e.name}{e.endDate && <StatusPill variant="default" label="Exited" />}</span>,
                        },
                        { key: 'designation', header: 'Designation', render: (e) => e.designation || '-' },
                        { key: 'grade', header: 'Grade', render: (e) => e.grade || '-' },
                        { key: 'unit', header: 'Unit', render: (e) => (e.orgUnitKey && unitByKey[e.orgUnitKey] ? unitByKey[e.orgUnitKey].name : '-') },
                        { key: 'manager', header: 'Reports To', render: (e) => (e.managerKey && empByKey[e.managerKey] ? empByKey[e.managerKey].name : '-') },
                        { key: 'type', header: 'Type', render: (e) => EMPLOYMENT_TYPE_LABELS[e.employmentType] || e.employmentType },
                      ]}
                      data={client.employees}
                    />
                  )}
                </Card>
              </div>
            )}

            {tab === 'ubo' && (
              <div className="tab-content">
                <Card>
                  <Card.Header
                    title="Ultimate Beneficial Owners"
                    subtitle={ubo ? `Effective ownership traced through the group. UBO threshold ${ubo.threshold}%.` : undefined}
                  />
                  {uboLoading ? (
                    <Skeleton.Text lines={4} />
                  ) : !ubo ? (
                    <EmptyState icon="🧭" title="Couldn't compute beneficial ownership" />
                  ) : ubo.effectiveOwners.length === 0 ? (
                    <EmptyState icon="🧭" title="No ownership recorded" description="Add owners on the Edit Client page to compute beneficial ownership." />
                  ) : (
                    <>
                      {/* Honest caveats before the numbers. */}
                      {ubo.hasCycle && (
                        <div className="form-error" role="alert">
                          A circular ownership link was detected and stopped — some percentages may be incomplete.
                        </div>
                      )}
                      {ubo.unaccountedPct > 0 && (
                        <p className="hint-text">
                          {ubo.unaccountedPct}% flows through group companies with no ownership recorded — shown below as unresolved.
                        </p>
                      )}
                      <Table
                        columns={[
                          {
                            key: 'name',
                            header: 'Beneficial Owner',
                            render: (o) => (
                              <span>
                                {o.name}
                                {o.isBeneficialOwner && <StatusPill variant="info" label="UBO" />}
                                {o.unresolved && <StatusPill variant="default" label="Unresolved" />}
                              </span>
                            ),
                          },
                          { key: 'holderType', header: 'Type', render: (o) => HOLDER_TYPE_LABELS[o.holderType] || o.holderType },
                          { key: 'pan', header: 'PAN', render: (o) => o.pan || '-' },
                          { key: 'effectivePct', header: 'Effective %', render: (o) => `${o.effectivePct}%` },
                        ]}
                        data={ubo.effectiveOwners}
                      />
                      {ubo.hasCorporateLayers && (
                        <p className="hint-text">
                          Traced through {ubo.maxDepth} corporate layer{ubo.maxDepth === 1 ? '' : 's'}. Effective % multiplies each holder's stake down the chain. A prompt, not a legal SBO determination.
                        </p>
                      )}
                    </>
                  )}
                </Card>

                {ubo && ubo.directOwners.length > 0 && (
                  <Card>
                    <Card.Header title="Direct Owners" subtitle="As recorded on this client, before tracing." />
                    <Table
                      columns={[
                        { key: 'name', header: 'Holder', render: (o) => <span>{o.name}{o.isLayered && <StatusPill variant="default" label="Group company" />}</span> },
                        { key: 'holderType', header: 'Type', render: (o) => HOLDER_TYPE_LABELS[o.holderType] || o.holderType },
                        { key: 'ownershipPct', header: 'Ownership', render: (o) => formatPct(o.ownershipPct) },
                      ]}
                      data={ubo.directOwners}
                    />
                  </Card>
                )}
              </div>
            )}

            {tab === 'expirables' && (
              <ExpirablesManager clientId={id} signatories={client.signatories || []} />
            )}

            {tab === 'charges' && (
              <div className="tab-content">
                {/* ── Register of charges ──────────────────────────────────
                    ⚠️ OPEN vs SATISFIED IS DERIVED from the satisfaction date
                    (the `status` virtual on the model), never a stored flag
                    somebody has to remember to flip. The open ones are what
                    matter: a charge whose loan was repaid years ago but whose
                    CHG-4 was never filed still encumbers the company, and it
                    is found in a due diligence rather than by the firm. */}
                <Card>
                  <Card.Header
                    title="Registered Charges"
                    subtitle={
                      (client.charges || []).length
                        ? `${client.charges.filter((c) => !c.satisfiedOn).length} open of ${client.charges.length}`
                        : 'Mortgages, hypothecations and liens filed with the RoC'
                    }
                    action={
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/org-admin/clients/${id}/edit`)}>
                        Edit Charges
                      </Button>
                    }
                  />
                  {(client.charges || []).length === 0 ? (
                    <EmptyState
                      icon="🏦"
                      title="No charges recorded"
                      description={
                        filesWithRoc(client)
                          ? 'Add each charge on the MCA index from the Edit Client page. A charge stays OPEN until a satisfaction date is entered — which is how one that was repaid but never closed becomes visible.'
                          : 'A charge register applies to companies and LLPs. Nothing to record for a client of this type.'
                      }
                    />
                  ) : (
                    <Table
                      columns={[
                        {
                          key: 'holderName',
                          header: 'Charge Holder',
                          render: (c) => (
                            <span>
                              {c.holderName}
                              {c.satisfiedOn
                                ? <StatusPill variant="success" label="Satisfied" />
                                : <StatusPill variant="warning" label="Open" />}
                            </span>
                          ),
                        },
                        { key: 'chargeType', header: 'Type', render: (c) => CHARGE_TYPE_LABELS[c.chargeType] || c.chargeType },
                        { key: 'amount', header: 'Amount', render: (c) => formatINR(c.amount) },
                        { key: 'chargeId', header: 'Charge ID', render: (c) => c.chargeId || '-' },
                        { key: 'createdOn', header: 'Created', render: (c) => fmtDate(c.createdOn) },
                        {
                          key: 'satisfiedOn',
                          header: 'Modified / Satisfied',
                          render: (c) => (c.satisfiedOn ? fmtDate(c.satisfiedOn) : fmtDate(c.modifiedOn)),
                        },
                        { key: 'propertyDescription', header: 'Property Charged', render: (c) => c.propertyDescription || '-' },
                      ]}
                      data={client.charges}
                      keyField="_id"
                    />
                  )}
                </Card>
              </div>
            )}

            {tab === 'turnover' && (
              <TurnoverManager clientId={id} client={client} onClientChange={onClientChange} />
            )}

            {tab === 'requests' && (
              <ClientRequestsManager clientId={id} />
            )}

            {tab === 'tally' && (
              <div className="tab-content">
                <TallyPanel clientId={id} />
              </div>
            )}

            {tab === 'enrollments' && (
              <div className="tab-content">
                <Card>
                  <Card.Header title="Enroll a Compliance Head" />
                  {enrollError && <div className="form-error" role="alert">{enrollError}</div>}
                  <form onSubmit={handleEnroll} className="enroll-form">
                    <div className="service-picker-field" ref={servicePickerRef}>
                      <label className="ds-field-label">
                        Compliance Type * — pick as many services as you want to enroll this client into at once
                      </label>
                      <div className="service-picker-search-row">
                        <Input
                          className="service-picker-search"
                          placeholder="🔎 Search services…"
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          // Clicking (or tabbing into) the search box is what opens
                          // the picker.
                          onFocus={() => setServicePickerOpen(true)}
                          onClick={() => setServicePickerOpen(true)}
                        />
                        {/* The view switch only means anything once the panel is
                            open, so it appears with it. */}
                        {servicePickerOpen && (
                          <div className="service-picker-view-toggle">
                            <button
                              type="button"
                              className={servicePickerView === 'list' ? 'is-active' : ''}
                              onClick={() => setServicePickerView('list')}
                              title="Compact list"
                            >
                              List
                            </button>
                            <button
                              type="button"
                              className={servicePickerView === 'cards' ? 'is-active' : ''}
                              onClick={() => setServicePickerView('cards')}
                              title="Category cards, with select-all and due rules"
                            >
                              Cards
                            </button>
                          </div>
                        )}
                        {canManageServices && (
                          <Button type="button" variant="secondary" onClick={openAddServiceModal}>
                            + Add Service
                          </Button>
                        )}
                      </div>

                      {!servicePickerOpen ? (
                        // Collapsed. Say what's already picked so the selection
                        // isn't invisible while the panel is shut.
                        <button
                          type="button"
                          className="service-picker-collapsed"
                          onClick={() => setServicePickerOpen(true)}
                        >
                          {enrollForm.selectedServiceIds.length > 0
                            ? `${enrollForm.selectedServiceIds.length} service${enrollForm.selectedServiceIds.length === 1 ? '' : 's'} selected — click to change`
                            : 'Click the search box to pick services'}
                        </button>
                      ) : servicePickerView === 'list' ? (
                        <div className="service-picker-box">
                          {serviceGroups.length === 0 ? (
                            <p className="empty-message">No services found</p>
                          ) : (
                            serviceGroups.map(({ displayName: groupName, items }) => (
                              <div key={groupName} className="service-picker-group">
                                <div className="service-picker-group-title">
                                  {groupName} <span className="service-picker-group-count">({items.length})</span>
                                </div>
                                {items.map((s) => (
                                  <label key={s._id} className="service-picker-row">
                                    <input
                                      type="checkbox"
                                      checked={enrollForm.selectedServiceIds.includes(s._id)}
                                      onChange={() => toggleEnrollService(s._id)}
                                    />
                                    <span>{s.name}</span>
                                  </label>
                                ))}
                              </div>
                            ))
                          )}
                        </div>
                      ) : serviceGroups.length === 0 ? (
                        <p className="empty-message">No services found</p>
                      ) : (
                        // Same card layout as the Services page (its stylesheet is
                        // imported below), so a category reads identically in both
                        // places — only here the checkbox means "enrol this client",
                        // not "the firm offers this".
                        <div className="services-groups service-picker-cards">
                          {serviceGroups.map(({ displayName: groupName, items }) => {
                            const chosen = items.filter((s) => enrollForm.selectedServiceIds.includes(s._id)).length;
                            return (
                              <Card key={groupName} className="service-group-card">
                                <div className="service-group-header">
                                  <div className="service-group-heading">
                                    <div className="service-group-name">{groupName}</div>
                                    <div className="service-group-subtitle">{chosen} of {items.length} selected</div>
                                  </div>
                                  <label className="service-group-all" title="Select all in this category">
                                    <input
                                      type="checkbox"
                                      checked={chosen === items.length}
                                      // A part-selected category shows a dash, not an
                                      // empty box — otherwise "none" and "some" look
                                      // the same in the header.
                                      ref={(el) => {
                                        if (el) el.indeterminate = chosen > 0 && chosen < items.length;
                                      }}
                                      onChange={(e) => toggleEnrollGroup(items, e.target.checked)}
                                    />
                                    <span>All</span>
                                  </label>
                                </div>

                                <div className="service-check-list">
                                  {items.map((s) => {
                                    const rule = s.effectiveDueRule
                                      || resolveEffectiveDueRule(s.defaultDueRule, s.dueRuleOverride);
                                    return (
                                      <div key={s._id} className="service-check-row">
                                        <label className="service-check-main">
                                          <input
                                            type="checkbox"
                                            className="service-check-box"
                                            checked={enrollForm.selectedServiceIds.includes(s._id)}
                                            onChange={() => toggleEnrollService(s._id)}
                                          />
                                          <span className="service-check-name">{formatServiceName(s.name)}</span>
                                        </label>
                                        {/* The due rule this enrolment will inherit. */}
                                        <div className="service-due">
                                          {rule ? (
                                            <span className="service-due-chip" title={describeDueRule(rule)}>
                                              {shortDueRule(rule)}
                                            </span>
                                          ) : (
                                            <span className="service-due-none" title="No due rule set — dates are filled in by hand">—</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      )}
                      {enrollForm.selectedServiceIds.length > 0 && (
                        <span className="service-picker-count">
                          {enrollForm.selectedServiceIds.length} selected
                        </span>
                      )}
                    </div>

                    {client.gstins.length > 0 && (
                      <div className="form-row">
                        <Select label="GSTIN (optional)" name="gstinId" value={enrollForm.gstinId} onChange={handleEnrollChange}>
                          <option value="">-- Not GSTIN-specific --</option>
                          {client.gstins.map((g) => (
                            <option key={g._id} value={g._id}>{g.gstin}</option>
                          ))}
                        </Select>
                      </div>
                    )}
                    <div className="form-row assignee-row">
                      <Select label="Preparer" name="defaultPreparerId" value={enrollForm.defaultPreparerId} onChange={handleEnrollChange}>
                        <option value="">-- None --</option>
                        {orgUsers.map((u) => (
                          <option key={u._id} value={u._id}>{u.name}</option>
                        ))}
                      </Select>
                      {enrollForm.defaultPreparerId && (
                        <Input
                          label="Preparer Hours"
                          type="number"
                          name="defaultPreparerHours"
                          min="0"
                          step="0.5"
                          placeholder="Est. hours/period"
                          value={enrollForm.defaultPreparerHours}
                          onChange={handleEnrollChange}
                        />
                      )}
                    </div>
                    <div className="form-row assignee-row">
                      <Select label="Reviewer" name="defaultReviewerId" value={enrollForm.defaultReviewerId} onChange={handleEnrollChange}>
                        <option value="">-- None --</option>
                        {orgUsers.map((u) => (
                          <option key={u._id} value={u._id}>{u.name}</option>
                        ))}
                      </Select>
                      {enrollForm.defaultReviewerId && (
                        <Input
                          label="Reviewer Hours"
                          type="number"
                          name="defaultReviewerHours"
                          min="0"
                          step="0.5"
                          placeholder="Est. hours/period"
                          value={enrollForm.defaultReviewerHours}
                          onChange={handleEnrollChange}
                        />
                      )}
                    </div>
                    <div className="assistants-field">
                      <label className="ds-field-label">
                        Assistants (optional) — juniors shadowing the preparer to help and learn. Set an
                        hour budget per person if you want to compare it against their actual logged hours later.
                      </label>
                      <div className="assistants-checkbox-list">
                        {orgUsers.map((u) => {
                          const checked = enrollForm.defaultAssistantIds.includes(u._id);
                          return (
                            <div key={u._id} className="assistant-row">
                              <label className="assistant-checkbox">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleAssistant(u._id)}
                                />
                                {u.name}
                              </label>
                              {checked && (
                                <input
                                  type="number"
                                  className="ds-input assistant-hours-input"
                                  min="0"
                                  step="0.5"
                                  placeholder="Hours"
                                  value={enrollForm.defaultAssistantHours[u._id] || ''}
                                  onChange={(e) => setAssistantHours(u._id, e.target.value)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <Button type="submit" variant="primary" loading={enrolling}>
                      {enrolling ? 'Enrolling…' : 'Enroll'}
                    </Button>
                  </form>
                </Card>

                <Card>
                  <Card.Header title="Enrolled Heads" />
                  {enrollments.length === 0 ? (
                    <EmptyState icon="📑" title="No compliance heads enrolled yet" />
                  ) : (
                    <div className="enrollments-list">
                      {enrollments.map((enrollment) => (
                        <div key={enrollment._id} className="enrollment-card">
                          <div className="enrollment-info">
                            <div className="enrollment-name-row">
                              <strong>{enrollment.complianceTypeId?.name}</strong>
                              <StatusPill variant="info" label={FREQUENCY_LABELS[enrollment.complianceTypeId?.frequency]} />
                            </div>
                            <div className="enrollment-meta">
                              {enrollment.defaultPreparerId && <span>Preparer: {enrollment.defaultPreparerId.name}</span>}
                              {enrollment.defaultReviewerId && <span>Reviewer: {enrollment.defaultReviewerId.name}</span>}
                              {enrollment.defaultAssistantIds?.length > 0 && (
                                <span>Assistants: {enrollment.defaultAssistantIds.map((a) => a.name).join(', ')}</span>
                              )}
                              {enrollment.defaultHourAllocations?.length > 0 && (
                                <span>
                                  Hours: {enrollment.defaultHourAllocations
                                    .map((a) => `${a.userId?.name} ${a.allocatedHours}h`)
                                    .join(', ')}
                                </span>
                              )}
                              {enrollment.complianceTypeId?.frequency && (
                                <span>
                                  {/* Not "N days before due" — that is untrue
                                      for a monthly head, which opens on the 1st
                                      of the due month. The sentence comes from
                                      the shared rule so it cannot drift. */}
                                  {describeProposalWindow(enrollment.complianceTypeId.frequency)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="enrollment-actions">
                            <StatusPill status={enrollment.isActive ? 'active' : 'dormant'} />
                            <Button variant="secondary" size="sm" onClick={() => toggleEnrollmentActive(enrollment)}>
                              {enrollment.isActive ? 'Pause' : 'Resume'}
                            </Button>
                            <button onClick={() => removeEnrollment(enrollment._id)} className="btn-remove">Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}

            {tab === 'calendar' && (
              <div className="tab-content">
                <Card>
                  <Card.Header
                    title="Compliance Instances"
                    action={
                      <Select value={instanceFilter} onChange={(e) => setInstanceFilter(e.target.value)} className="instance-filter">
                        <option value="all">All Statuses</option>
                        <option value="upcoming">Upcoming</option>
                        <option value="in_progress">In Progress</option>
                        <option value="filed">Filed</option>
                        <option value="late_filed">Late Filed</option>
                        <option value="not_applicable">Not Applicable</option>
                      </Select>
                    }
                  />
                  {visibleInstances.length === 0 ? (
                    <EmptyState
                      icon="📅"
                      title={instances.length === 0 ? 'No compliance instances yet' : 'No instances match this filter'}
                      description={instances.length === 0 ? 'Enroll a compliance head to generate the calendar.' : undefined}
                    />
                  ) : (
                    <Table
                      columns={instanceColumns}
                      data={visibleInstances}
                      rowClassName={(i) => (isOverdue(i) ? 'ds-table-row-overdue' : '')}
                    />
                  )}
                </Card>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <Modal
        isOpen={addServiceModalOpen}
        onClose={() => setAddServiceModalOpen(false)}
        title="Add Service"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddServiceModalOpen(false)} disabled={addServiceSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAddServiceSave} loading={addServiceSaving}>
              {addServiceSaving ? 'Saving…' : 'Add Service'}
            </Button>
          </>
        }
      >
        <form className="service-form" onSubmit={handleAddServiceSave}>
          {addServiceError && <div className="form-error" role="alert">{addServiceError}</div>}

          <Input
            label="Group *"
            name="groupName"
            list="client-detail-service-group-options"
            placeholder="e.g. GST, Income Tax, Payroll"
            value={addServiceForm.groupName}
            onChange={(e) => setAddServiceForm({ ...addServiceForm, groupName: e.target.value })}
            disabled={addServiceSaving}
          />
          <datalist id="client-detail-service-group-options">
            {Array.from(new Set(services.map((s) => s.groupName))).map((g) => (
              <option key={g} value={g} />
            ))}
          </datalist>

          <Input
            label="Service Name *"
            name="name"
            placeholder="e.g. GST REFUND ON EXPORT OF GOODS"
            value={addServiceForm.name}
            onChange={(e) => setAddServiceForm({ ...addServiceForm, name: e.target.value })}
            disabled={addServiceSaving}
          />
        </form>
      </Modal>
    </OrgAdminLayout>
  );
}

export default ClientDetail;
